import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { presenceService } from '../services/presenceService';

interface UsePresenceOptions {
  enabled?: boolean;
  currentPage?: string;
  groupId?: string;
}

export const usePresence = (options: UsePresenceOptions = {}) => {
  const { currentUser } = useAuth();
  const { enabled = true, currentPage = 'community', groupId } = options;
  const initializeOnceRef = useRef(false);
  const lastPageRef = useRef<string | undefined>(undefined);
  const lastGroupRef = useRef<string | undefined>(undefined);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Performance: Memoize the update function to prevent unnecessary re-renders
  const updateLocation = useCallback((page: string, group?: string) => {
    if (enabled && currentUser && initializeOnceRef.current) {
      presenceService.updateUserLocation(page, group);
    }
  }, [enabled, currentUser]);

  // Performance: Memoize the cleanup function
  const setOffline = useCallback(() => {
    if (enabled && initializeOnceRef.current) {
      presenceService.setUserOffline();
      initializeOnceRef.current = false;
    }
  }, [enabled]);

  // Initialize presence when user logs in and component mounts
  useEffect(() => {
    if (!enabled || !currentUser || initializeOnceRef.current) return;

    const initializePresence = async () => {
      try {
        await presenceService.setUserOnline(
          currentUser.uid,
          currentUser.displayName || currentUser.email || 'Unknown',
          currentPage,
          groupId
        );
        initializeOnceRef.current = true;
        lastPageRef.current = currentPage;
        lastGroupRef.current = groupId;
      } catch (error) {
        console.error('Failed to initialize presence:', error);
      }
    };

    initializePresence();

    // Cleanup on unmount or user logout
    return () => {
      if (initializeOnceRef.current) {
        // Performance: Add a small delay to prevent rapid cleanup/reinit cycles
        cleanupTimeoutRef.current = setTimeout(() => {
          presenceService.setUserOffline();
          initializeOnceRef.current = false;
        }, 1000);
      }
    };
  }, [currentUser, enabled]);

  // Update presence when page or group changes
  useEffect(() => {
    if (!enabled || !currentUser || !initializeOnceRef.current) return;

    const shouldUpdate = 
      lastPageRef.current !== currentPage || 
      lastGroupRef.current !== groupId;

    if (shouldUpdate) {
      const updatePresence = async () => {
        try {
          // Check if this is a group transition (joining/leaving group)
          const isGroupTransition = lastGroupRef.current !== groupId;
          
          // Force update for group transitions to bypass throttling
          await presenceService.updateUserLocation(currentPage, groupId, isGroupTransition);
          
          // Performance: Cleanup old group listeners when switching groups
          const previousGroupId = lastGroupRef.current;
          if (previousGroupId && previousGroupId !== groupId) {
            presenceService.cleanupGroupListener(previousGroupId);
          }
          
          lastPageRef.current = currentPage;
          lastGroupRef.current = groupId;
        } catch (error) {
          console.error('Failed to update presence location:', error);
        }
      };

      updatePresence();
    }
  }, [currentPage, groupId, enabled, currentUser]);

  // Cleanup when user logs out
  useEffect(() => {
    if (!currentUser && initializeOnceRef.current) {
      presenceService.setUserOffline();
      initializeOnceRef.current = false;
    }
  }, [currentUser]);

  // Performance: Clear cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, []);

  return {
    isOnline: enabled && !!currentUser && initializeOnceRef.current,
    updateLocation,
    setOffline
  };
}; 